// Where the exported art lives (public/art, written by art-src/export.py from Art/UI Redesign/Source). Keep every path here
// so a renamed export changes one line.
const A = "/art";
const SF = `${A}/map/slate-fire`;
const TN = `${A}/map/terrain-natural-v1`;
const PW = `${A}/map/painted-world-v2`;

export const art = {
  avatar: `${A}/ui/avatar-2x.webp`,
  nav: {
    portal: `${A}/nav/portal-2x.webp`,
    map: `${A}/nav/map-2x.webp`,
    guild: `${A}/nav/guild-2x.webp`,
    store: `${A}/nav/store-2x.webp`,
    quests: `${A}/nav/quests-2x.webp`,
  },
  hero: {
    portal: `${A}/hero/portal.webp`,
    home: `${A}/hero/home.webp`,
    cityHall: `${A}/hero/city-hall.webp`,
    warehouse: `${A}/hero/warehouse.webp`,
    lumberMill: `${A}/hero/lumber-mill.webp`,
    farm: `${A}/hero/farm.webp`,
    forge: `${A}/hero/forge.webp`,
    quarry: `${A}/hero/quarry.webp`,
    ironMine: `${A}/hero/iron-mine.webp`,
    hidingPlace: `${A}/hero/hiding-place.webp`,
    walls: `${A}/hero/walls.webp`,
    barracks: `${A}/hero/barracks.webp`,
    stables: `${A}/hero/stables.webp`,
    workshop: `${A}/hero/workshop.webp`,
    academy: `${A}/hero/academy.webp`,
    rally: `${A}/hero/rally.webp`,
    market: `${A}/hero/market.webp`,
    treasury: `${A}/hero/treasury.webp`,
    queueKeep: `${A}/hero/queue-keep.webp`,
    wheelHall: `${A}/hero/wheel-hall.webp`,
    loyalty: `${A}/hero/loyalty.webp`,
    settings: `${A}/hero/settings.webp`,
    reports: `${A}/hero/reports.webp`,
    mail: `${A}/hero/mail.webp`,
    guild: `${A}/hero/guild.webp`,
    leaderboard: `${A}/hero/leaderboard.webp`,
  },
  /** Phase 3: the wax seals on report rows and the (one, for now) guild crest */
  social: {
    seal: (kind: "victory" | "losses" | "defeat" | "trade" | "support" | "spy" | "incoming" | "guild") =>
      `${A}/social/seal-${kind === "incoming" ? "spy" : kind === "guild" ? "support" : kind}.webp`,
    crest: `${A}/social/crest.webp`,
  },
  /** building medallions by building key (+ "wheel") */
  med: (key: string) => `${A}/med/${key}.webp`,
  /** troop icons (art-src/export_unit_icons.py, Oct 11 2026): freestanding cut-outs, one per troop, used wherever a troop is
   *  shown (UnitMedal); the tier is the disc beside it, not the picture */
  unitIcon: (slug: string) => `${A}/unit/icon-${slug}.webp`,
  /** unit faces: face-<slug>-<tier>.webp; the Scholar has one face (no longer shown since the Oct 11 2026 icons) */
  unitFace: (slug: string, tier = 1) => (slug === "scholar" ? `${A}/unit/face-scholar.webp` : `${A}/unit/face-${slug}-${Math.min(3, Math.max(1, tier))}.webp`),
  /** unit paintings for the recruit hero: art-<slug>-<tier>.webp */
  unitArt: (slug: string, tier = 1) => (slug === "scholar" ? `${A}/unit/art-scholar.webp` : `${A}/unit/art-${slug}-${Math.min(3, Math.max(1, tier))}.webp`),
  /** recruit-screen clips (art-src/export_unit_videos.py, Oct 8 2026): video-<slug>.mp4 + its first frame as the poster.
   *  Units not listed keep their painting. w/h = the clip's size, so the stage can lay it out before it loads. */
  unitVideos: {
    lancer: { src: `${A}/unit/video-lancer.mp4`, poster: `${A}/unit/video-lancer.webp`, w: 768, h: 768 },
    sentry: { src: `${A}/unit/video-sentry.mp4`, poster: `${A}/unit/video-sentry.webp`, w: 768, h: 768 },
    berserker: { src: `${A}/unit/video-berserker.mp4`, poster: `${A}/unit/video-berserker.webp`, w: 704, h: 684 },
    ram: { src: `${A}/unit/video-ram.mp4`, poster: `${A}/unit/video-ram.webp`, w: 760, h: 434 },
    scout: { src: `${A}/unit/video-scout.mp4`, poster: `${A}/unit/video-scout.webp`, w: 768, h: 768 },
    knight: { src: `${A}/unit/video-knight.mp4`, poster: `${A}/unit/video-knight.webp`, w: 726, h: 768 },
    guardian: { src: `${A}/unit/video-guardian.mp4`, poster: `${A}/unit/video-guardian.webp`, w: 742, h: 742 },
    ballista: { src: `${A}/unit/video-ballista.mp4`, poster: `${A}/unit/video-ballista.webp`, w: 768, h: 768 },
  } as Record<string, { src: string; poster: string; w: number; h: number }>,
  store: {
    buy_all: `${A}/store/buy_all.webp`, buy_wood: `${A}/store/buy_wood.webp`, buy_clay: `${A}/store/buy_clay.webp`, buy_iron: `${A}/store/buy_iron.webp`,
    boost_wood: `${A}/store/boost_wood.webp`, boost_clay: `${A}/store/boost_clay.webp`, boost_iron: `${A}/store/boost_iron.webp`,
    speedup15: `${A}/store/speedup15.webp`, speedup60: `${A}/store/speedup60.webp`, speedup150: `${A}/store/speedup150.webp`, speedup480: `${A}/store/speedup480.webp`,
    truce: `${A}/store/truce.webp`, shield: `${A}/store/shield.webp`, medics: `${A}/store/medics.webp`, loot: `${A}/store/loot.webp`, prestige: `${A}/store/prestige.webp`,
    reveal_city: `${A}/store/reveal_city.webp`, reveal_incoming: `${A}/store/reveal_incoming.webp`, queue_extender: `${A}/store/queue_extender.webp`,
    mass_scholarships: `${A}/store/mass_scholarships.webp`, amulet: `${A}/store/amulet.webp`, npc_trade: `${A}/store/npc_trade.webp`,
  } as Record<string, string>,
  pack: (key: string) => `${A}/store/pack-${key}.webp`,
  academy: {
    buffer: `${A}/academy/buffer.webp`, crown: `${A}/academy/crown.webp`, decree: `${A}/academy/decree.webp`, farm: `${A}/academy/farm.webp`,
    premium: `${A}/academy/premium.webp`, prestige: `${A}/academy/prestige.webp`, scroll: `${A}/academy/scroll.webp`,
  },
  market: { cart: `${A}/market/cart.webp` },
  city: {
    /** The Hearthstone Refined city (City View handoff, site version 20, Oct 12 2026): the shared terrain and every layer
     *  of the master, cropped to its painted pixels by art-src/export_hearthstone_city.py into
     *  public/art/city/hearthstone-refined (hashes in art-src/hearthstone-refined/manifest.json; the old exporters can't
     *  write there). Where each file goes, and when: game/cityScene.ts (hearthstoneScene.json). */
    hearthstone: (file: string) => `${A}/city/hearthstone-refined/${file}`,
    terrain: `${A}/city/hearthstone-refined/terrain.png`,
    countryside: `${A}/city/countryside.webp`,
    /** the same backdrop at 3x, swapped in once the city is zoomed in from the overview (CityView paint) */
    countrysideHD: `${A}/city/countryside-hd.webp`,
  },
  map: {
    /** the Oct 7 2026 keep art: only the player profile's city medallions still use it (the map moved to slateFire) */
    keep: (tier: 1 | 2 | 3 | 4) => `${A}/map/keep-${tier}.webp`,
    /** The Slate & Fire map (approved Oct 11 2026, site version 12): the handoff's PNGs, copied verbatim from
     *  Art/UI Redesign/UI Refresh/Map/assets into public/art/map/slate-fire (hashes in art-src/slate-fire-map/manifest.json).
     *  art-src/export_map.py never writes here. */
    slateFire: {
      /** the ready-coloured castle for a relationship and tier (the owned castles carry the richer gold roofs) */
      castle: (rel: "you" | "guild" | "ally" | "nap" | "enemy" | "unaligned", tier: 1 | 2 | 3 | 4) => `${SF}/cities/${rel}/castle-${tier}.png`,
      /** the uncoloured castle (part of the approved set; the map no longer draws it — an NPC city wears its stockade) */
      base: (tier: 1 | 2 | 3 | 4) => `${SF}/base/castle-${tier}.png`,
      /** a barbarian (NPC) city: the Log Stockade of its tier, 1536 x 1024 with its own foundation, in its authored colours
       *  and with no pennant (Ben's choice, Oct 12 2026; copied verbatim from Art/UI Redesign/UI Refresh/Map/NPC Log Stockade,
       *  provenance in art-src/slate-fire-map/npc-log-stockade/manifest.json) */
      npc: (tier: 1 | 2 | 3 | 4) => `${SF}/npc/log-stockade/npc-log-stockade-tier-${tier}.png`,
      /** the selected city's broad ivory glow, baked at 2x around a castle 220 logical px wide (135 px padding) */
      glow: (tier: 1 | 2 | 3 | 4) => `${SF}/selection/castle-${tier}-glow.png`,
      /** the painted swallowtail pennant: cloth only, 160 x 104 (pole and finial are drawn) */
      flag: (rel: "you" | "guild" | "ally" | "nap" | "enemy" | "unaligned") => `${SF}/flags/flag-${rel}.png`,
      /** the mossy ground, a seamless 768-px tile spanning 4 cells */
      terrain: `${SF}/terrain/terrain.png`,
      scenery: {
        pines3: `${SF}/scenery/scenery-pines3.png`,
        pines3b: `${SF}/scenery/scenery-pines3b.png`,
        oaks_mixed: `${SF}/scenery/scenery-oaks_mixed.png`,
        firs: `${SF}/scenery/scenery-firs.png`,
        boulders: `${SF}/scenery/scenery-boulders.png`,
      },
      /** painted heater shields at the castle's foundation corner: steel + gold sunburst = beginner protection, ivory + gold
       *  olive branch = truce (the app's own shields, exported as PNG) */
      shieldProtection: `${SF}/badges/shield-protection.png`,
      shieldTruce: `${SF}/badges/shield-truce.png`,
    },
    /** Natural lake and ravine materials + accents (Oct 14 2026, Valor/output/terrain-sprites-natural-v1): copied byte for
     *  byte into public/art/map/terrain-natural-v1, provenance and manifest in art-src/terrain-natural-v1. The four materials
     *  are surface texture only — render.ts's fields still own where water and gorges are; the two accents are NW-lit and
     *  are never mirrored or rotated. */
    terrainNatural: {
      lakeWater: `${TN}/lake-water.png`,
      lakeShore: `${TN}/lake-shore.png`,
      ravineRock: `${TN}/ravine-rock.png`,
      ravineFloor: `${TN}/ravine-floor.png`,
      lakeStone: `${TN}/lake-stone.png`,
      ravineRimRock: `${TN}/ravine-rim-rock.png`,
    },
    /** Painted world v2 (Oct 15 2026, Valor/output/map-painted-world-v2): runtime cuts of the package's atlases made by
     *  art-src/map-painted-world-v2/export.py — castles per relationship and tier, pennants, materials, shore and gorge-wall
     *  relief, timber bridges, trees, rocks and reeds. The file names and metadata are in src/map/paintedWorldData.ts. */
    paintedWorld: (file: string) => `${PW}/${file}`,
    /** The animated army march markers (Oct 15 2026): one atlas of eight walking frames per movement kind at 2x and 3x,
     *  made by art-src/export_army.py from the concept walk atlas; metadata in src/map/armyData.ts. */
    army: (file: string) => `${A}/map/army/${file}`,
  },
  icon: {
    shield: `${A}/ui/icon-shield-2x.webp`,
    swords: `${A}/ui/icon-swords-2x.webp`,
    star: `${A}/ui/icon-star-2x.webp`,
    castle: `${A}/ui/icon-castle-2x.webp`,
  },
  hud: {
    wood: `${A}/hud/wood-2x.webp`,
    clay: `${A}/hud/clay-2x.webp`,
    iron: `${A}/hud/iron-2x.webp`,
    food: `${A}/hud/food-2x.webp`,
    pop: `${A}/hud/pop-2x.webp`,
  },
  /** the swipe city-switch's cloud cover and arrows — the live game's own sprites (Front End Assets/Resources/SwipeOverlay) */
  swipe: {
    cloud: `${A}/swipe/cloud.webp`,
    cloudDark: `${A}/swipe/cloud-dark.webp`,
    cloudLightning: `${A}/swipe/cloud-lightning.webp`,
    arrowNext: `${A}/swipe/arrow-next.webp`,
    arrowPrev: `${A}/swipe/arrow-prev.webp`,
  },
} as const;
